import { Table } from '@radix-ui/themes'
import { flexRender, Row } from '@tanstack/react-table'
import React from 'react'
import { css, styled } from 'styled-components'

const StyledTableCell = styled(Table.Cell)`
  color: ${({ theme }) => theme.forecast.defaultText};
  padding: 0.5rem;
  text-align: left;
  vertical-align: middle;
  min-height: 3.5rem;
  border-style: hidden !important;
  white-space: nowrap;
`

const StyledFirstCell = styled(Table.Cell)`
  display: flex;
  align-items: center;
`

const StyledTableRow = styled(Table.Row)<{
  $hasOnClick: boolean
  $depth?: number
  $enableExpanding?: boolean
}>`
  background-color: ${({ theme }) => theme.forecast.cardBackground};
  position: relative;

  ${({ $hasOnClick }) =>
    $hasOnClick &&
    css`
      &:hover {
        ${StyledTableCell} {
          background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
        }

        &:hover * {
          cursor: pointer;
        }
      }

      &:active {
        ${StyledTableCell} {
          background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
        }
      }
    `}

  ${({ $depth = 0, $enableExpanding }) =>
    $depth > 0 &&
    css`
      ${StyledTableCell}:first-child {
        padding-left: ${$depth * 1.5}rem;
        position: relative;

        ${$enableExpanding &&
        css`
          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: ${$depth * 1.5}rem;
            background-color: ${({ theme }) => theme.forecast.pageBackground1};
            z-index: 0;
          }
        `}
      }
    `}
`

const ExpandButton = styled.button<{ $isPlaceholder?: boolean }>`
  background: none;
  border: none;
  margin-right: 0.25rem;
  margin-left: 0.5rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  ${({ $isPlaceholder }) =>
    $isPlaceholder &&
    css`
      opacity: 0;
    `}
`

interface TableBodyRowProps<TData extends object> {
  row: Row<TData>
  enableExpanding?: boolean
  onRowClick?: (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
}

export const TableBodyRow = <TData extends object>({
  row,
  enableExpanding,
  onRowClick,
}: TableBodyRowProps<TData>) => {
  const canExpand = enableExpanding && row.getCanExpand()
  const isExpanded = enableExpanding && row.getIsExpanded()

  const handleRowClick = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (enableExpanding && canExpand) {
      row.toggleExpanded()
    }

    if (onRowClick) {
      onRowClick(row, event)
    }
  }

  return (
    <StyledTableRow
      key={row.id}
      data-state={row.getIsSelected() && 'selected'}
      onClick={handleRowClick}
      $hasOnClick={Boolean(onRowClick || (enableExpanding && canExpand))}
      $depth={row.depth}
      $enableExpanding={enableExpanding}>
      {row.getVisibleCells().map((cell, index) => (
        <StyledTableCell key={cell.id}>
          {index === 0 && enableExpanding ? (
            <StyledFirstCell>
              <ExpandButton
                onClick={(e) => {
                  e.stopPropagation()
                  row.toggleExpanded()
                }}
                $isPlaceholder={!canExpand}
                aria-label={isExpanded ? 'Collapse row' : 'Expand row'}>
                {isExpanded ? '▼' : '▶'}
              </ExpandButton>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </StyledFirstCell>
          ) : (
            flexRender(cell.column.columnDef.cell, cell.getContext())
          )}
        </StyledTableCell>
      ))}
    </StyledTableRow>
  )
}
